import { useEffect, useRef, useState } from 'react';

// Hook for scroll animations
export const useScrollAnimation = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  const {
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px',
    triggerOnce = true
  } = options;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce && ref.current) {
            observer.unobserve(ref.current);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [ref, isVisible];
};

// Animation wrapper component
export const ScrollReveal = ({ 
  children, 
  animation = 'fade-up',
  delay = 0,
  duration = 600,
  className = '',
  threshold = 0.1,
  ...props 
}) => {
  const [ref, isVisible] = useScrollAnimation({ threshold });

  const animations = {
    'fade-up': {
      hidden: { opacity: 0, transform: 'translateY(30px)' },
      visible: { opacity: 1, transform: 'translateY(0)' }
    },
    'fade-down': {
      hidden: { opacity: 0, transform: 'translateY(-30px)' },
      visible: { opacity: 1, transform: 'translateY(0)' }
    },
    'fade-left': {
      hidden: { opacity: 0, transform: 'translateX(-30px)' },
      visible: { opacity: 1, transform: 'translateX(0)' }
    },
    'fade-right': {
      hidden: { opacity: 0, transform: 'translateX(30px)' },
      visible: { opacity: 1, transform: 'translateX(0)' }
    },
    'fade': {
      hidden: { opacity: 0 },
      visible: { opacity: 1 }
    },
    'scale': {
      hidden: { opacity: 0, transform: 'scale(0.9)' },
      visible: { opacity: 1, transform: 'scale(1)' }
    },
    'scale-up': {
      hidden: { opacity: 0, transform: 'scale(0.8) translateY(20px)' },
      visible: { opacity: 1, transform: 'scale(1) translateY(0)' }
    }
  };

  const anim = animations[animation] || animations['fade-up'];
  const currentStyle = isVisible ? anim.visible : anim.hidden;

  return (
    <div
      ref={ref}
      className={`scroll-reveal ${className}`}
      style={{
        ...currentStyle,
        transition: `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms ease-out ${delay}ms`,
        willChange: 'opacity, transform'
      }}
      {...props}
    >
      {children}
    </div>
  );
};

// Staggered children animation
export const StaggerContainer = ({ 
  children, 
  staggerDelay = 100,
  animation = 'fade-up',
  duration = 600,
  className = '',
  ...props 
}) => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <div ref={ref} className={className} {...props}>
      {Array.isArray(children) 
        ? children.map((child, index) => (
            <ScrollReveal
              key={index}
              animation={animation}
              delay={isVisible ? index * staggerDelay : 0}
              duration={duration}
            >
              {child}
            </ScrollReveal>
          ))
        : children
      }
    </div>
  );
};

export default ScrollReveal;
